<script setup lang="ts">
import TemplateManager from '@/components/domain/TemplateManager.vue'

import { useEnvironmentStore } from '@/stores/environmentStore'

import {
  type ChangePasswordForm,
  type OrganizationMembership,
  WhatNowContext
} from '@/types'
import { onMounted, ref } from 'vue'
import Tabs, { type TabDescriptor } from '@/components/layout/Tabs.vue'
import Settings from '@/components/user/Settings.vue'
import { useRouter } from 'vue-router'
import useModuleStore from '@/stores/moduleStore'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import userService from '@/services/user.service'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast({ position: 'bottom', queue: true, duration: 1000 })
const router = useRouter()
const moduleStore = useModuleStore()
const environmentStore = useEnvironmentStore()
environmentStore.title = 'page-titles.profile'
environmentStore.setContextId(WhatNowContext.Profile)
const { user, isAuthenticated } = storeToRefs(environmentStore)
const currentOrganization = ref<OrganizationMembership>()
const isLoaded = ref(false)

onMounted(() => {
  if (!isAuthenticated || !user.value) {
    router.push({ name: 'login' })
  } else {
    if (user) {
      if (user.value.currentOrganizationMembership)
        currentOrganization.value = user.value.currentOrganizationMembership
    }
  }

  isLoaded.value = true
})

function logout() {
  environmentStore.logout()
  moduleStore.reset()
  router.push({ name: 'login' })
}

const descriptors: TabDescriptor[] = [
  { heading: 'label.user-info', templateName: 'userInfo' },
  { heading: 'label.modules', templateName: 'module-management' },
  { heading: 'label.settings', templateName: 'settings' }
]

const getUserRoles = () => {
  if (!user.value || !user.value.currentOrganizationMembership) {
    return ''
  }

  return user.value.currentOrganizationMembership.roles.map((role) => role).join(', ')
}

const changeDetected = () => {
  return (
    currentOrganization.value?.organizationId !=
    user.value?.currentOrganizationMembership.organizationId
  )
}

const update = () => {
  toast.info('Oppdatering av brukerinformasjon er ikke implementert')
}

const passwordForm = ref<ChangePasswordForm>({
  oldPassword: '',
  newPassword: '',
  newPasswordConfirm: ''
})

const changePassword = (form: ChangePasswordForm) => {
  userService
    .changePassword(form.oldPassword, form.newPassword, form.newPasswordConfirm)
    .then((result) => {
      if (result) {
        toast.success(t('toast.password-changed'))
        resetPasswordForm()
      } else {
        toast.error(t('toast.technical-error'))
      }
    })
}

const resetPasswordForm = () => {
  passwordForm.value.oldPassword = ''
  passwordForm.value.newPassword = ''
  passwordForm.value.newPasswordConfirm = ''
}

function validatePasswordForm($event: Event): void {
  $event.preventDefault()
  let errors: string[] = []
  if (passwordForm.value.oldPassword.trim().length <= 5) {
    errors.push('Eksisterende passord må være minst 6 tegn')
  }

  if (passwordForm.value.newPassword.trim().length <= 5) {
    errors.push('Nytt passord må være minst 6 tegn')
  }

  if (passwordForm.value.newPassword.trim() != passwordForm.value.newPasswordConfirm.trim()) {
    errors.push('Passordene stemmer ikke overens')
  }

  if (errors.length > 0) {
    toast.error(errors.join('<br/> '))
    return
  }

  changePassword(passwordForm.value)
}
</script>

<template>
  <div class="profile-container" v-if="isLoaded">
    <div class="actions">
      <button @click="logout" class="warn">{{ $t('label.logout') }}</button>
      <button @click="update" class="ok" :disabled="!changeDetected()">
        {{ $t('label.update') }}
      </button>
    </div>
    <Tabs :tabDescriptors="descriptors">
      <template #userInfo>
        <h2>{{ $t('label.user-info') }}</h2>
        <div class="column">
          <label for="username">{{ $t('label.username') }}</label>
          <input id="username" :value="user?.username" readonly />
        </div>
        <div class="column">
          <label for="organization">{{ $t('label.organization') }}</label>
          <input
            id="organization"
            :value="user?.currentOrganizationMembership?.organizationName"
            readonly
          />
        </div>

        <div class="divider"></div>
        <div v-if="user && user.organizationMemberships && user.organizationMemberships.length > 1">
          <h3>{{ $t('label.organization') }}</h3>
          <div class="column">
            <label for="membershipSelector"> </label>
            <select v-model="currentOrganization" id="membershipSelector">
              <option
                v-for="membership in user?.organizationMemberships"
                :value="membership"
                :key="membership.organizationId"
              >
                {{ membership.organizationName }}
              </option>
            </select>
          </div>
        </div>
        <div class="wrapper-row">
          <h3>{{ $t('label.change-password') }}</h3>
          <form v-on:submit="changePassword(passwordForm)">
            <div class="column">
              <label for="old_password">{{ $t('label.current-password') }}</label>
              <input id="old_password" type="password" v-model="passwordForm.oldPassword" />
            </div>
            <div class="column">
              <label for="new_password">{{ $t('label.new-password') }}</label>
              <input id="new_password" type="password" v-model="passwordForm.newPassword" />
            </div>
            <div class="column">
              <label for="new_password_confirm">{{ $t('label.new-password-repeat') }}</label>
              <input
                id="new_password_confirm"
                type="password"
                v-model="passwordForm.newPasswordConfirm"
              />
            </div>
            <div class="action">
              <button type="submit" class="continue" @click="validatePasswordForm">
                {{ $t('label.update-password') }}
              </button>
            </div>
          </form>
        </div>
      </template>
      <template #module-management>
        <div class="template-manager-container">
          <h1>{{ $t('module.available-modules') }}</h1>
          <TemplateManager></TemplateManager>
        </div>
      </template>
      <template #settings>
        <Settings></Settings>
      </template>
    </Tabs>
  </div>
</template>

<style scoped>
.action {
  margin-top: 8px;
}

.profile-container {
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  align-content: space-between;
  height: 100%;
  scrollbar-width: none;
  overflow-y: hidden;
}

.divider {
  margin: 16px 0;
}

select {
  margin-top: 8px;
}

.template-manager-container {
  display: flex;
  flex-direction: column;
}

button:disabled {
  cursor: not-allowed;
  background-color: #a9a9a9;
  border-bottom: 1px solid #a9a9a9;
}

</style>
